import { capitalize, getSectionDisplayNames } from '@tunasong/models'
import { ChordLib } from '@tunasong/music-lib'
import type { Chord } from '@tunasong/music-lib'

/** One line of a version as plain text (GH #2695 spec §2). A heading is a section name, shown as context. */
export interface VersionLine {
  text: string
  heading?: boolean
}

interface SlateLike {
  type?: unknown
  text?: unknown
  name?: unknown
  children?: unknown
  sectionType?: unknown
}

/** The inline elements in the editor's plugins: they sit inside a line instead of making one */
const inlineTypes = new Set(['chord', 'mention', 'link', 'entitylink', 'emoji', 'effect'])

const asNode = (node: unknown): SlateLike => (node && typeof node === 'object' ? (node as SlateLike) : {})

const childrenOf = (node: SlateLike): unknown[] => (Array.isArray(node.children) ? node.children : [])

const isText = (node: SlateLike) => typeof node.text === 'string'

const isInline = (node: SlateLike) => typeof node.type === 'string' && inlineTypes.has(node.type)

/** A chord reads as `[C]` before the syllable it is on, the way a chord chart is written in plain text */
const inlineText = (node: SlateLike): string => {
  if (isText(node)) {
    return String(node.text)
  }
  if (node.type === 'chord') {
    return `[${ChordLib.getName(node as unknown as Chord)}]`
  }
  if (node.type === 'mention' && typeof node.name === 'string') {
    return `@${node.name}`
  }
  return childrenOf(node)
    .map(child => inlineText(asNode(child)))
    .join('')
}

/** A block whose children are only text and inline elements is one line */
const isLineBlock = (node: SlateLike) =>
  childrenOf(node).every(child => isText(asNode(child)) || isInline(asNode(child)))

const sectionTypesOf = (nodes: unknown[]): unknown[] =>
  nodes.flatMap(node => {
    const element = asNode(node)
    return element.type === 'section' ? [element.sectionType] : sectionTypesOf(childrenOf(element))
  })

/**
 * A version's content as lines of plain text: one per block, with chords inline and section names as headings, so a
 * chord change and a lyric change read the same way. Media blocks show their name ("Image: cover.jpg").
 */
export const versionLines = (content?: unknown[] | null): VersionLine[] => {
  if (!content) {
    return []
  }
  const sectionNames = getSectionDisplayNames(
    sectionTypesOf(content).map(type => (typeof type === 'string' ? type : undefined))
  )
  let sectionIndex = 0

  const linesOf = (node: SlateLike): VersionLine[] => {
    if (node.type === 'section') {
      const heading: VersionLine = { text: sectionNames[sectionIndex] ?? 'Section', heading: true }
      sectionIndex += 1
      return [heading, ...childrenOf(node).flatMap(child => linesOf(asNode(child)))]
    }
    if (!isLineBlock(node)) {
      return childrenOf(node).flatMap(child => linesOf(asNode(child)))
    }
    const text = inlineText(node).trim()
    if (text) {
      return [{ text }]
    }
    if (typeof node.type === 'string' && typeof node.name === 'string' && node.name) {
      return [{ text: `${capitalize(node.type)}: ${node.name}` }]
    }
    return []
  }

  return content.flatMap(node => linesOf(asNode(node)))
}
